import { useEffect, useRef, useState, type FC } from "react";
import { useCreateOrderFromContractStore } from "@/stores/createOrderFromContractStore";
import { useTranslation } from "react-i18next";
import ModalHeader from "@/components/atoms/ModalHeader";
import Modal, { ModalCTAs } from "../../molecules/Modal";
import Tooltip from "@/components/atoms/Tooltip";
import Stepper from "../../molecules/Stepper";
import { useStepperStore } from "@/stores/stepperStore";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import styles from "./styles.module.css";
import { contractsUIConfig, getContractTradeType } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import CTA from "@/components/atoms/CTA";
import { OrderFromContractStepTypeEnum } from "@/constants/enums/StepTypeEnum";
import DetailsStep from "./steps/DetailsStep";
import {
  Address,
  Contract,
  ContractLight,
  Country,
  CreateOrderFromContract as CreateOrderFromContractType,
  CreateOrderValue,
  CreditOrderCheckRequest,
  CurrencyCode,
  ISODateTimeString,
  Unit,
} from "@/types";
import {
  applyServicePriceOverrides,
  createOrderFromContractPlaceholder,
  getMappedServicesWithContractPricing,
  getTransportAddressIds,
  mapOrderCreationError,
} from "./helpers";
import LocationAndDeliveryStep from "./steps/LocationAndDeliveryStep";
import { useGetContractById } from "@/services/Contracts";
import DocumentsStep from "./steps/DocumentsStep";
import VerificationStep from "./steps/VerificationStep";
import { useGetCountries } from "@/services/RegisterCompany";
import { postCheckCreditOrder, postCreateOrderFromContract, useGetCreateOrderValues } from "@/services/Orders";
import { useGetCompanySpecificServices } from "@/services/Company";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import useIndexedRef from "@/hooks/useIndexedRef";
import { buildPickupCoordinates } from "@/utils/coordinates";
import axios from "axios";

export interface CreateOrderFromContractValues {
  companyId: number;
  userId: number;
  amount: number;
  unit: number;
  orderAddress: null;
  contractId: number;
  boxtypeId: number;
  orderCurrency: CurrencyCode;
  orderNeedsApproval: boolean;
  /** Service prices the user edited in the details step, keyed by companySpecificServiceId. */
  servicePriceOverrides: Record<number, number>;
  estimatedDelivery: Date | ISODateTimeString | null;
  created: Date | ISODateTimeString;
  batchNumber: string | null;
  differentAddress: boolean;
  address: string;
  postCode: string;
  countryId: number;
  place: string;
  selectedDocuments: string[];
  latitude: string;
  longitude: string;
  capturedAt: string | null;
  /** Set when the receiver has order planning enabled. Sent to BE as receptionSlotId. */
  receptionSlotId: number | null;
  /** The counterpart's shipping address picked in step 2; null keeps the contract's address. */
  selectedCounterpartAddress: Address | null;
}

const CreateOrderFromContract: FC = () => {
  const [order, setOrder] = useState<CreateOrderFromContractValues>({} as CreateOrderFromContractValues);
  const [showLowCreditWarning, setShowLowCreditWarning] = useState<boolean>(false);
  const close = useCreateOrderFromContractStore((s) => s.close);
  const contractType = useCreateOrderFromContractStore((s) => s.contractType);
  const contract = useCreateOrderFromContractStore((s) => s.contract);
  const isOpen = useCreateOrderFromContractStore((s) => s.isOpen);
  const currentStep = useStepperStore((s) => s.currentStep);
  const setCurrentStep = useStepperStore((s) => s.goTo);
  const next = useStepperStore((s) => s.next);
  const prev = useStepperStore((s) => s.prev);
  const steps = useStepperStore((s) => s.steps);
  const setSteps = useStepperStore((s) => s.setSteps);
  const queryClient = useQueryClient();

  const { data: fullContractData, isLoading: isFullContractLoading } = useGetContractById(contract?.id as number, {
    enabled: contractType === "contractLight" && !!contract,
  });

  const fullContract = contractType === "contractLight" && fullContractData ? fullContractData : (contract as Contract);

  const { currentCompany, user } = useAuth();

  const { data: countryOptions } = useGetCountries(user?.language.id as number, {
    enabled: !!user,
  });
  const { data: createOrderValues } = useGetCreateOrderValues();
  const { t } = useTranslation();
  const formSections = useRef<(HTMLFormElement | null)[]>([]);

  const contractTradeType = getContractTradeType(contract!, currentCompany);
  const { whiteIconName, createOrderModalHeader } = contractsUIConfig(contractTradeType);

  const { data: companySpecificServicesData, isLoading: isServicesLoading } = useGetCompanySpecificServices(
    contract?.createdByCompany as number,
    false,
    {
      enabled: !!contract?.createdByCompany,
    }
  );

  // A ContractLight (opened from the contracts list) carries no services, so the wizard isn't ready to move on
  // until the full contract has loaded.
  const isContractDataLoading = isFullContractLoading || isServicesLoading;

  const contractServices = getMappedServicesWithContractPricing(companySpecificServicesData, fullContract);
  const orderServices = applyServicePriceOverrides(contractServices, order.servicePriceOverrides ?? {});

  useEffect(() => {
    const orderPlaceHolder = createOrderFromContractPlaceholder(
      currentCompany?.companyId,
      user?.id,
      contract as ContractLight
    );
    if (orderPlaceHolder) {
      setOrder(orderPlaceHolder as CreateOrderFromContractValues);
    }
  }, [currentCompany, user, contract]);

  useEffect(() => {
    const totalSteps = 4;
    const defaultSteps = 3;

    if (isOpen) {
      setSteps(totalSteps);
    }

    return () => {
      setSteps(defaultSteps);
    };
  }, [isOpen, setSteps]);

  const validateCurrentStep = () => {
    const form = formSections.current[currentStep];
    if (form) {
      form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    }
  };

  const onFormSectionValidationSuccess = async () => {
    if (currentStep === steps - 1) {
      await executeCreateOrder(order);
    } else {
      next();
    }
  };

  const { mutateAsync: mutateCreateOrder, isPending: isCreateOrderPending } = useMutation({
    mutationFn: async ({ servicePriceOverrides, ...data }: CreateOrderFromContractValues) => {
      const selectedCountry = countryOptions?.find((country) => country.id === order.countryId) as Country;
      const selectedUnit = createOrderValues?.units.find((unit) => unit.id === order.unit);
      const translatedRequest: CreateOrderFromContractType = {
        ...data,
        orderCurrency: createOrderValues?.currencies.find(
          (currency) => currency.value.toUpperCase() === data.orderCurrency.toUpperCase()
        ) as CreateOrderValue,
        created: dayjs(data.created).toISOString(),
        estimatedDelivery: dayjs(data.estimatedDelivery).toISOString(),
        companySpecificServices: applyServicePriceOverrides(contractServices, servicePriceOverrides ?? {}),
        orderAddress: {
          address: order.address,
          postCode: order.postCode,
          place: order.place,
          countryCodeIso2: selectedCountry?.iso2,
          countryCodeIso3: selectedCountry?.iso3,
          isEu: selectedCountry?.isEu,
        },
        unit: {
          unit: selectedUnit?.value,
          id: selectedUnit?.id,
        } as Unit,
        attachments: order.selectedDocuments,
        pickupCoordinates: buildPickupCoordinates(order.latitude, order.longitude, order.capturedAt),
        receptionSlotId: data.receptionSlotId ?? null,
        ...getTransportAddressIds(contractTradeType, data.selectedCounterpartAddress?.id),
      };
      await postCreateOrderFromContract(translatedRequest);
    },
    onSuccess: async () => {
      close();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.createSuccess"), t("notifications:orders.orderCreated"))
      );
      await queryClient.invalidateQueries({ queryKey: ["getContractsLight"] });
      await queryClient.invalidateQueries({
        queryKey: ["getContractById", contract?.id],
      });
    },
    onError: (error, variables) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const reason = mapOrderCreationError(status, variables.receptionSlotId);

      if (reason === "slotUnavailable" || reason === "slotFull" || reason === "slotFractionMismatch") {
        queryClient.invalidateQueries({ queryKey: ["receptionSlots"], exact: false });
      }

      const messageKey = reason === "generic" ? "orders.orderCreatedError" : `orders.${reason}`;
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.createError"), t(`notifications:${messageKey}`))
      );
    },
  });

  const { mutateAsync: mutateCheckCreditOrder } = useMutation({
    mutationFn: async (data: CreditOrderCheckRequest) => {
      const response = await postCheckCreditOrder(data);
      return response.data;
    },
  });

  const executeCreateOrder = async (data: CreateOrderFromContractValues) => {
    if (!fullContract.tradeAtOwnRisk) {
      const amountUnit = createOrderValues?.units.find((unit) => unit.id === data.unit);
      if (!amountUnit) return;

      // Fix for no-non-null-asserted-optional-chain: assign priceUnit to a variable
      const priceUnit = contract?.contractProduct.priceUnit;

      if (contractTradeType === ContractTradeTypeEnum.SELL) {
        const creditCheckData: CreditOrderCheckRequest = {
          buyerCompanyId: fullContract.contractBuyers[0].id,
          amount: data.amount,
          amountUnit: { id: amountUnit.id, unit: amountUnit.value },
          price: fullContract.contractProduct.agreedBuyerPricePrUnit[fullContract.contractProduct.currencyType],
          priceCurrency: fullContract.contractProduct.currencyType,
          priceUnit: priceUnit as Unit,
        };
        const creditCheckResponse = await mutateCheckCreditOrder(creditCheckData);

        if (creditCheckResponse.warmLowCredit) {
          setShowLowCreditWarning(true);
          return;
        } else {
          await mutateCreateOrder(data);
        }
      } else {
        await mutateCreateOrder(data);
      }
    } else {
      await mutateCreateOrder(data);
    }
  };

  function closeWizard() {
    setCurrentStep(0);
    close();
  }

  const onChangeCreateOrderFromContractValues = (changed: Partial<CreateOrderFromContractValues>) => {
    setOrder((prev) => ({ ...prev, ...changed }));
  };

  return (
    <Modal
      opened={isOpen}
      onClose={closeWizard}
      size="xl"
      closeOnClickOutside={false}
      title={<ModalHeader title={t(createOrderModalHeader)} svgAsset={whiteIconName} />}
    >
      <Stepper
        active={currentStep}
        onStepClick={setCurrentStep}
        allowNextStepsSelect={false}
        classNames={{
          stepIcon: styles.stepIcon,
          step: styles.step,
          stepLabel: styles.stepLabel,
          separator: styles.separator,
          steps: styles.stepperHeader,
          content: styles.stepperContent,
        }}
        size="sm"
        completedIcon={<SVGAsset name="step-complete" />}
      >
        <Stepper.Step
          label={t("orders:details")}
          icon={
            <Tooltip label={t("orders:details")}>
              <span>1</span>
            </Tooltip>
          }
        >
          <DetailsStep
            ref={useIndexedRef<HTMLFormElement>(formSections, 0)}
            onValidationSuccess={onFormSectionValidationSuccess}
            onChangeWizardData={onChangeCreateOrderFromContractValues}
            wizardData={order}
            services={orderServices}
          />
        </Stepper.Step>

        <Stepper.Step
          label={t("orders:locationAndDelivery")}
          icon={
            <Tooltip label={t("orders:locationAndDelivery")}>
              <span>2</span>
            </Tooltip>
          }
        >
          <LocationAndDeliveryStep
            ref={useIndexedRef<HTMLFormElement>(formSections, 1)}
            onValidationSuccess={onFormSectionValidationSuccess}
            onChangeWizardData={onChangeCreateOrderFromContractValues}
            contract={fullContract}
          />
        </Stepper.Step>

        <Stepper.Step
          label={t("orders:documents")}
          icon={
            <Tooltip label={t("orders:documents")}>
              <span>3</span>
            </Tooltip>
          }
        >
          <DocumentsStep
            ref={useIndexedRef<HTMLFormElement>(formSections, 2)}
            onValidationSuccess={onFormSectionValidationSuccess}
            onChangeWizardData={onChangeCreateOrderFromContractValues}
            wizardData={order}
          />
        </Stepper.Step>

        <Stepper.Step
          label={t("orders:verifyOrder")}
          icon={
            <Tooltip label={t("orders:verifyOrder")}>
              <span>4</span>
            </Tooltip>
          }
        >
          <VerificationStep
            ref={useIndexedRef<HTMLFormElement>(formSections, 3)}
            onValidationSuccess={onFormSectionValidationSuccess}
            showLowCreditWarning={showLowCreditWarning}
            wizardData={order}
            services={orderServices}
            contract={fullContract}
          />
        </Stepper.Step>
      </Stepper>
      <ModalCTAs>
        <CTA id="cancel-create-contract" intent="secondary" onClick={() => close()}>
          {t("actions:cancel")}
        </CTA>

        {currentStep > 0 && (
          <CTA id="back-create-sales-order" intent="secondary" onClick={prev}>
            {t("actions:back")}
          </CTA>
        )}

        <CTA
          id="next-create-sales-order"
          intent="primary"
          onClick={validateCurrentStep}
          loading={isCreateOrderPending || isContractDataLoading}
        >
          {currentStep !== OrderFromContractStepTypeEnum.VERIFY_ORDER ? t("actions:next") : t("actions:create")}
        </CTA>
      </ModalCTAs>
    </Modal>
  );
};

export default CreateOrderFromContract;
